import { useCallback } from "react";

import { useMutation } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import type { ChangeSolutionStepsType } from "app/components/ChangeSolutionModal/consts";
import { changeSolutionSteps, SWITCH_SOLUTION_KEY } from "app/components/ChangeSolutionModal/consts";
import { getNextStep, getPreviousStep, trackEvents } from "app/components/ChangeSolutionModal/hooks/utils";
import type { IUseCaseStepConfig, IStepConfig, SolutionType } from "app/components/ChangeSolutionModal/types";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { UPDATE_STARTUP_FORCE } from "app/UtilitiesAndConstants/Constants/queryParams";

import { changeSolutionApiService } from "../services/changeSolutionApiService";

interface IUseChangeSolutionFlow {
    currentStep: ChangeSolutionStepsType;
    steps: Record<ChangeSolutionStepsType, IStepConfig>;
    selectedSolution?: IUseCaseStepConfig;
    defaultSolution: IUseCaseStepConfig | null;
    setCurrentStep: (step: ChangeSolutionStepsType) => void;
    onClose: () => void;
    trackingEventName: string;
}

export const useChangeSolutionFlow = ({
    currentStep,
    selectedSolution,
    defaultSolution,
    setCurrentStep,
    onClose,
    trackingEventName,
}: IUseChangeSolutionFlow) => {
    const switchSolutionMutation = useMutation({
        mutationFn: (solutionType: SolutionType) => changeSolutionApiService.switchSolution(solutionType),
        onSuccess: () => {
            onClose();
            window.location.replace(`${window.location.origin}?${UPDATE_STARTUP_FORCE}=true`);
        },
        onError: (error) => {
            Sentry.errorTracker({
                e: error,
                message: "Error when trying to switch solution",
                level: "error",
            });
        },
    });
    const handleNext = () => {
        trackEvents("click", `${trackingEventName}/next/${selectedSolution && selectedSolution.value}`);
        const nextStep = getNextStep(currentStep, selectedSolution, defaultSolution);
        if (nextStep !== null) {
            setCurrentStep(nextStep);
        }
    };

    const handleBack = () => {
        const previousStep = getPreviousStep(currentStep);
        if (previousStep !== null) {
            setCurrentStep(previousStep);
        }
    };

    const handleComplete = async () => {
        await PreferencesService.add({ [SWITCH_SOLUTION_KEY]: true });
        if (currentStep === changeSolutionSteps.currentSolution) {
            trackEvents("continue", `${trackingEventName}/keeping trial solution`);
            onClose();
            return;
        }

        if (currentStep === changeSolutionSteps.newSolution) {
            trackEvents("confirm", `${trackingEventName}/${selectedSolution && selectedSolution.value}`);

            if (selectedSolution) {
                await switchSolutionMutation.mutateAsync(selectedSolution.solution);
            }
        }
    };

    const handleClose = useCallback(async () => {
        onClose();
        await PreferencesService.add({ [SWITCH_SOLUTION_KEY]: true });
    }, [onClose]);

    return {
        handleNext,
        handleBack,
        handleComplete,
        handleClose,
        isSwitchingSolution: switchSolutionMutation.isPending,
        switchSolutionError: switchSolutionMutation.error,
    };
};
